import apiClient from './meta-client';
import type {
    NodeResponse,
    NodeCreate,
    NodeUpdate,
    TriggerNodeConfig,
    ButtonCreate,
    ButtonUpdate,
} from '@/types/node';

// ========== NODES API ==========

// Obter node específico
export const getNode = async (nodeId: number): Promise<NodeResponse> => {
    const response = await apiClient.get<NodeResponse>(`/nodes/${nodeId}`);
    return response.data;
};

// Criar node
export const createNode = async (funnelId: number, data: NodeCreate): Promise<NodeResponse> => {
    const response = await apiClient.post<NodeResponse>(`/funnels/${funnelId}/nodes`, data);
    return response.data;
};

// Atualizar node
export const updateNode = async (nodeId: number, data: NodeUpdate): Promise<NodeResponse> => {
    // Converte do formato frontend para o formato do backend
    const payload: Record<string, any> = {};

    // Nome: aceita node_name (outros configs) ou nome (TagConfig envia na raiz)
    if (data.node_name !== undefined) payload.nome = data.node_name;
    if (data.nome !== undefined) payload.nome = data.nome;

    // tag_id na raiz (ex.: TagConfig envia { nome, tag_id })
    if (data.tag_id !== undefined) payload.tag_id = data.tag_id;

    // go_to na raiz (GoToConfig envia { nome, goto_funnel_id, goto_trigger_node_id })
    if (data.goto_funnel_id !== undefined) payload.goto_funnel_id = data.goto_funnel_id;
    if (data.goto_trigger_node_id !== undefined) payload.goto_trigger_node_id = data.goto_trigger_node_id;

    // Mídia e delay na raiz (PhotoConfig/VideoConfig enviam { nome, media_url, media_filename, attachment_id, delay_seconds })
    if (data.media_url !== undefined) payload.media_url = data.media_url;
    if (data.media_filename !== undefined) payload.media_filename = data.media_filename;
    if (data.attachment_id !== undefined) payload.attachment_id = data.attachment_id;
    if (data.media_type !== undefined) payload.media_type = data.media_type;
    if (data.delay_seconds !== undefined) payload.delay_seconds = data.delay_seconds;

    // Extrai campos do config para o nível raiz
    if (data.config) {
        if (data.config.message !== undefined) payload.message = data.config.message;
        if (data.config.title !== undefined) payload.title = data.config.title;
        if (data.config.subtitle !== undefined) payload.subtitle = data.config.subtitle;
        if (data.config.media_url !== undefined) payload.media_url = data.config.media_url;
        if (data.config.media_type !== undefined) payload.media_type = data.config.media_type;
        if (data.config.media_filename !== undefined) payload.media_filename = data.config.media_filename;
        if (data.config.delay_seconds !== undefined) payload.delay_seconds = data.config.delay_seconds;
        if (data.config.attachment_id !== undefined) payload.attachment_id = data.config.attachment_id;
        // Origem + enquadramento do recorte: nao vao para a Meta, so permitem
        // reabrir o editor no ponto em que ficou.
        if (data.config.media_source_url !== undefined) payload.media_source_url = data.config.media_source_url;
        if (data.config.media_crop !== undefined) payload.media_crop = data.config.media_crop;
        if (data.config.tag_id !== undefined) payload.tag_id = data.config.tag_id;
    }

    // Blocos compostos - enviar como JSON string
    if (data.config?.blocks !== undefined) {
        payload.blocks = JSON.stringify(data.config.blocks);
    }

    // Campos que já vêm no nível raiz
    if (data.position_x !== undefined) payload.position_x = data.position_x;
    if (data.position_y !== undefined) payload.position_y = data.position_y;
    if (data.is_random !== undefined) payload.is_random = data.is_random;

    if (Object.keys(payload).length === 0) {
        throw new Error('Nenhum campo para atualizar. Envie pelo menos um campo (ex.: nome, tag_id).');
    }

    const response = await apiClient.put<NodeResponse>(`/nodes/${nodeId}`, payload);
    return response.data;
};

// Deletar node
export const deleteNode = async (nodeId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.delete<{ message: string; success: boolean }>(`/nodes/${nodeId}`);
    return response.data;
};

// Configurar trigger node
export const configureTriggerNode = async (
    nodeId: number,
    data: TriggerNodeConfig
): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.post<{ message: string; success: boolean }>(
        `/nodes/${nodeId}/configure-trigger`,
        {
            nome: data.node_name,
            triggers: data.triggers,
            is_random: data.is_random ?? false,
            is_sequence: data.is_sequence ?? false,
        }
    );
    return response.data;
};

// ========== BUTTONS API ==========

// Criar botão
export const createButton = async (nodeId: number, data: ButtonCreate): Promise<any> => {
    const response = await apiClient.post(`/nodes/${nodeId}/buttons`, data);
    return response.data;
};

// Atualizar botão
export const updateButton = async (
    buttonId: number,
    data: ButtonUpdate
): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.put<{ message: string; success: boolean }>(
        `/buttons/${buttonId}`,
        data
    );
    return response.data;
};

// Deletar botão
export const deleteButton = async (buttonId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.delete<{ message: string; success: boolean }>(`/buttons/${buttonId}`);
    return response.data;
};

// Reordenar botões
export const reorderButtons = async (nodeId: number, buttonOrder: number[]) => {
    const payload = { button_order: buttonOrder };  // ✅ Envia como objeto
    console.log('🔍 Payload reorder:', payload); // DEBUG

    const response = await apiClient.post(`/nodes/${nodeId}/buttons/reorder`, payload);
    return response.data;
};
